import React, { useCallback, useEffect, useState } from "react";
import { Tag, Button, message, Modal, Descriptions, Divider, Empty, Input, Select, Checkbox, Popconfirm, Segmented, Table } from "antd";
import {
  AppstoreOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileOutlined,
  GlobalOutlined,
  IdcardOutlined,
  PoweroffOutlined,
  MailOutlined,
  PhoneOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  CalendarOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  ShoppingOutlined,
  ShareAltOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import "./QRList.css";
import { QR_BACKGROUND_COLORS, QR_COLORS, QR_CONFIGS } from "../../config/qrFormConfig";
import HtmlEditor from "../../components/HtmlEditor";
import { downloadCanvasPng, downloadQrAttachment, safeFileName } from "../../utils/downloadFile";

const parsePayload = (payloadData) => {
  if (!payloadData) return {};
  try {
    return typeof payloadData === "string" ? JSON.parse(payloadData) : payloadData;
  } catch {
    return {};
  }
};

const sanitizeHtml = (html = "") =>
  html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
const BRAND_LOGO_TYPES = ["image/jpeg", "image/png"];
const BRAND_LOGO_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const MESSAGE_IMAGE_TYPES = ["image/jpeg", "image/png"];
const MESSAGE_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const DATE_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const isSupportedBrandLogo = (file) =>
  file &&
  (BRAND_LOGO_TYPES.includes(file.type) ||
    BRAND_LOGO_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension)));
const isSupportedMessageImage = (file) =>
  file &&
  (MESSAGE_IMAGE_TYPES.includes(file.type) ||
    MESSAGE_IMAGE_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension)));
const formatDisplayDate = (value = "") => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${DATE_MONTHS[Number(month) - 1]}-${year}`;
};
const parseDisplayDate = (value = "") => {
  const match = value.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
  if (!match) return value;
  const monthIndex = DATE_MONTHS.findIndex((month) => month.toLowerCase() === match[2].toLowerCase());
  if (monthIndex < 0) return value;
  return `${match[3]}-${String(monthIndex + 1).padStart(2, "0")}-${match[1]}`;
};

const QRList = () => {
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [selectedQR, setSelectedQR] = useState(null);
  const [editQR, setEditQR] = useState(null);
  const [editPasswordError, setEditPasswordError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ type: "", status: "" });
  const [viewMode, setViewMode] = useState("Table");

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  }), []);

  const getQRType = (record) => record.qrType || (record.link ? "Website" : "Text");
  const isLinkType = (qrType) => ["Website", "Brand", "Social", "Product", "Location"].includes(qrType);
  const getPublicQRUrl = (record) => record.qrUrl || (record.uniqueId ? `https://www.bonoxi.com/qr/${record.uniqueId}` : "");
  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts.length ? parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase() : "VC";
  };
  const productPlatforms = ["Amazon", "Flipkart", "Myntra", "Shopify", "Meesho", "Custom Store"];
  const getProductPlatform = (platform = "") => productPlatforms.includes(platform) ? platform : "";
  const getFileName = (url = "") => {
    try {
      return decodeURIComponent(url.split("/").pop()?.split("?")[0] || "File");
    } catch {
      return "File";
    }
  };
  const getFileExtension = (file = {}) => (file.fileExtension || getFileName(file.fileUrl || "").split(".").pop() || "FILE").replace(".", "").toUpperCase();
  const isImageFile = (url = "") => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url.split("?")[0]);
  const formatFileSize = (bytes = 0) => {
    if (!bytes) return "Unknown";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const normalizeQRDetail = (payload, fallback) => ({
    ...(payload?.qrCode || payload || fallback),
    files: payload?.files || [],
  });

  const fetchQRDetail = async (record) => {
    if (!record?.id) return record;
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Qr/${record.id}`, {
        headers: getAuthHeaders(),
      });
      return normalizeQRDetail(res.data?.data, record);
    } catch {
      message.error("Failed to fetch QR detail");
      return record;
    }
  };

  const fetchQRList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", pageSize: "20" });
      if (filters.type) params.append("type", filters.type);
      if (filters.status) params.append("status", filters.status);

      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Qr?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const payload = res.data.data;
      const qrItems = Array.isArray(payload) ? payload : payload?.data || [];
      setData(qrItems);
      setTotal(Array.isArray(payload) ? qrItems.length : payload?.total || qrItems.length);
    } catch { message.error("Failed to fetch QR list"); }
    finally { setLoading(false); }
  }, [filters, getAuthHeaders]);

  useEffect(() => { fetchQRList(); }, [fetchQRList]);

  const formatDate = (d) => {
    try { return new Date(d.split(".")[0]).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }); }
    catch { return d; }
  };

  const handleDownload = async (record) => {
    const downloadName = safeFileName(record.title || "qr-code", "png");
    const id = record.id || record.uniqueId || record.title;

    if (record.id) {
      try {
        await downloadQrAttachment({
          baseUrl: process.env.REACT_APP_BASE_URL,
          id: record.id,
          token: localStorage.getItem("token") || "",
          title: record.title || "qr-code",
          format: "png",
        });
      } catch {
        message.error("Unable to download high-resolution QR. Please try again.");
      }
      return;
    }

    const canvas = document.getElementById(`qr-canvas-${id}`);
    if (canvas) {
      downloadCanvasPng(canvas, downloadName);
    }
  };

  const copyQRLink = async (event, url) => {
    event.preventDefault();
    event.stopPropagation();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      message.success("QR link copied");
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      message.success("QR link copied");
    }
  };

  const openDetail = async (record) => {
    setSelectedQR(await fetchQRDetail(record));
  };

  const openEdit = async (record) => {
    const detail = await fetchQRDetail(record);
    setEditPasswordError("");
    const payload = parsePayload(detail.payloadData);
    const qrType = getQRType(detail);
    const config = QR_CONFIGS[qrType] || {};
    setEditQR({
      ...detail,
      editPayload: payload,
      editPrimaryValue: config.primaryKey
        ? detail.link || payload[config.primaryKey] || payload.url || payload.link || payload.websiteUrl || payload.mapsUrl || payload.socialUrl || payload.productUrl || ""
        : "",
      editPlatform: payload.platform || config.platformOptions?.[0] || "",
      editSubject: payload.subject || "",
      editDescription: detail.qrText || payload[config.descriptionKey || "description"] || "",
      brandCompanyName: payload.companyName || "",
      brandWebsiteUrl: detail.link || payload.websiteUrl || payload.url || "",
      brandLogoFile: null,
      brandLogoPreviewUrl: "",
      messageImageFile: null,
      messageImagePreviewUrl: "",
      password: "",
      confirmPassword: "",
      removePasswordProtection: false,
    });
  };

  const saveEdit = async () => {
    if (!editQR?.id) return;
    setEditPasswordError("");
    if (editQR.password && editQR.password.length < 6) {
      const errorText = "Password must be at least 6 characters.";
      setEditPasswordError(errorText);
      message.error(errorText);
      return;
    }
    if (editQR.password && editQR.password !== editQR.confirmPassword) {
      const errorText = "Password and confirm password must match.";
      setEditPasswordError(errorText);
      message.error(errorText);
      return;
    }
    if (getQRType(editQR) === "Brand" && editQR.brandLogoFile && !isSupportedBrandLogo(editQR.brandLogoFile)) {
      message.warning("Brand logo supports JPG, JPEG, and PNG only.");
      return;
    }
    if (getQRType(editQR) === "Message" && editQR.messageImageFile && !isSupportedMessageImage(editQR.messageImageFile)) {
      message.warning("Message picture supports JPG, JPEG, and PNG only.");
      return;
    }

    const qrType = getQRType(editQR);
    const config = QR_CONFIGS[qrType] || {};
    const nextPayload = { ...(editQR.editPayload || parsePayload(editQR.payloadData)) };
    nextPayload.qrType = qrType;
    nextPayload.title = editQR.title || "";
    nextPayload.source = editQR.source || nextPayload.source || "Web";
    if (config.primaryKey) {
      nextPayload[config.primaryKey] = qrType === "Brand" ? editQR.brandWebsiteUrl || "" : editQR.editPrimaryValue || "";
    }
    if (config.platformOptions && editQR.editPlatform) {
      nextPayload.platform = editQR.editPlatform;
    }
    if (config.subjectLabel) {
      nextPayload.subject = editQR.editSubject || "";
    }
    if (config.descriptionLabel) {
      nextPayload[config.descriptionKey || "description"] = editQR.editDescription || "";
    }

    const body = {
      title: editQR.title || "",
      link: config.primaryKey ? (qrType === "Brand" ? editQR.brandWebsiteUrl || "" : editQR.editPrimaryValue || "") : editQR.link || "",
      qrText: config.descriptionLabel ? editQR.editDescription || "" : editQR.qrText || "",
      payloadData: JSON.stringify(nextPayload),
      color: editQR.color || "#000000",
      backgroundColor: editQR.backgroundColor || "#FFFFFF",
      size: editQR.size || "Medium",
      status: editQR.status || "Active",
    };

    if (qrType === "Brand") {
      body.qrType = "Brand";
      body.source = editQR.source || "Web";
      body.url = editQR.brandWebsiteUrl || "";
      body.websiteUrl = editQR.brandWebsiteUrl || "";
      body.companyName = editQR.brandCompanyName || nextPayload.companyName || "";
    }
    if (qrType === "Message") {
      body.qrType = "Message";
      body.source = editQR.source || "Web";
      body.subject = editQR.editSubject || "";
      body.description = editQR.editDescription || "";
    }

    if (editQR.password) {
      body.password = editQR.password;
      body.confirmPassword = editQR.confirmPassword;
    }
    if (editQR.removePasswordProtection) {
      body.removePasswordProtection = true;
    }

    setSaving(true);
    try {
      let res;
      const hasBrandLogoFile = qrType === "Brand" && editQR.brandLogoFile instanceof File;
      const hasMessageImageFile = qrType === "Message" && editQR.messageImageFile instanceof File;

      if (hasBrandLogoFile || hasMessageImageFile) {
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        if (hasBrandLogoFile) {
          formData.append("logo", editQR.brandLogoFile);
        }
        if (hasMessageImageFile) {
          formData.append("file", editQR.messageImageFile);
        }
        res = await axios.put(`${process.env.REACT_APP_BASE_URL}/api/Qr/${editQR.id}`, formData, {
          headers: getAuthHeaders(),
        });
      } else {
        res = await axios.put(`${process.env.REACT_APP_BASE_URL}/api/Qr/${editQR.id}`, body, {
          headers: getAuthHeaders(),
        });
      }
      if (res.data?.error === false) {
        message.success(res.data?.message || "QR updated successfully");
        setEditQR(null);
        setEditPasswordError("");
        fetchQRList();
      } else {
        message.error(res.data?.message || "Failed to update QR");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update QR");
    } finally {
      setSaving(false);
    }
  };

  const selectBrandLogo = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) return;
    if (!isSupportedBrandLogo(nextFile)) {
      message.warning("Brand logo supports JPG, JPEG, and PNG only.");
      event.target.value = "";
      setEditQR((prev) => ({
        ...prev,
        brandLogoFile: null,
        brandLogoPreviewUrl: "",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEditQR((prev) => ({
        ...prev,
        brandLogoFile: nextFile,
        brandLogoPreviewUrl: reader.result || "",
      }));
    };
    reader.readAsDataURL(nextFile);
  };

  const selectMessageImage = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) return;
    if (!isSupportedMessageImage(nextFile)) {
      message.warning("Message picture supports JPG, JPEG, and PNG only.");
      event.target.value = "";
      setEditQR((prev) => ({
        ...prev,
        messageImageFile: null,
        messageImagePreviewUrl: "",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEditQR((prev) => ({
        ...prev,
        messageImageFile: nextFile,
        messageImagePreviewUrl: reader.result || "",
      }));
    };
    reader.readAsDataURL(nextFile);
  };

  const updateEditPayload = (key, value) => {
    setEditQR((prev) => ({
      ...prev,
      editPayload: {
        ...(prev.editPayload || {}),
        [key]: value,
      },
      ...(key === "companyName" ? { brandCompanyName: value } : {}),
      ...(key === "websiteUrl" ? { brandWebsiteUrl: value, editPrimaryValue: value, link: value } : {}),
    }));
  };

  const renderEditTypeFields = () => {
    if (!editQR) return null;

    const qrType = getQRType(editQR);
    const config = QR_CONFIGS[qrType] || {};
    const hasSharedConfig = Boolean(QR_CONFIGS[qrType]);

    if (!hasSharedConfig) {
      return isLinkType(qrType) ? (
        <>
          <label>Link</label>
          <Input value={editQR.link} onChange={(event) => setEditQR((prev) => ({ ...prev, link: event.target.value }))} />
        </>
      ) : (
        <>
          <label>Text</label>
          <Input.TextArea rows={4} value={editQR.qrText} onChange={(event) => setEditQR((prev) => ({ ...prev, qrText: event.target.value }))} />
          <label>Payload Data</label>
          <Input.TextArea rows={4} value={editQR.payloadData || ""} onChange={(event) => setEditQR((prev) => ({ ...prev, payloadData: event.target.value }))} />
        </>
      );
    }

    const updatePrimaryValue = (value) => {
      setEditQR((prev) => ({
        ...prev,
        editPrimaryValue: value,
        ...(qrType === "Brand" ? { brandWebsiteUrl: value, link: value } : {}),
      }));
      if (config.primaryKey) {
        updateEditPayload(config.primaryKey, value);
      }
    };

    return (
      <>
        {qrType === "Brand" && (
          <>
            <div className="qrl-edit-brand-summary">
              <div className="qrl-edit-brand-logo">
                {editQR.brandLogoPreviewUrl || editQR.logoUrl ? (
                  <img src={editQR.brandLogoPreviewUrl || editQR.logoUrl} alt={editQR.title || "Brand logo"} />
                ) : (
                  <GlobalOutlined />
                )}
              </div>
              <div>
                <strong>{editQR.brandCompanyName || editQR.title || "Brand QR"}</strong>
                <span>{editQR.brandLogoFile ? "New logo selected" : editQR.logoUrl ? "Logo uploaded" : "No logo uploaded"}</span>
              </div>
            </div>

            <label>{config.fileLabel || "Logo"}</label>
            <Input type="file" accept={config.fileAccept || "image/*"} onChange={selectBrandLogo} />
          </>
        )}

        {config.primaryKey && (
          <>
            <label>{config.primaryLabel || "URL"}</label>
            <Input
              value={qrType === "Brand" ? editQR.brandWebsiteUrl : editQR.editPrimaryValue}
              placeholder={config.primaryPlaceholder}
              onChange={(event) => updatePrimaryValue(event.target.value)}
            />
          </>
        )}

        {config.platformOptions && (
          <>
            <label>{config.platformLabel || "Platform"}</label>
            <Select
              value={editQR.editPlatform}
              onChange={(platform) => {
                setEditQR((prev) => ({ ...prev, editPlatform: platform }));
                updateEditPayload("platform", platform);
              }}
              options={config.platformOptions.map((platform) => ({ value: platform, label: platform }))}
            />
          </>
        )}

        {config.subjectLabel && (
          <>
            <label>{config.subjectLabel}</label>
            <Input
              value={editQR.editSubject}
              placeholder={config.subjectPlaceholder || "Subject"}
              onChange={(event) => setEditQR((prev) => ({ ...prev, editSubject: event.target.value }))}
            />
          </>
        )}

        {config.descriptionLabel && (
          <>
            <label>{config.descriptionLabel}</label>
            <HtmlEditor
              value={editQR.editDescription || ""}
              onChange={(value) => setEditQR((prev) => ({ ...prev, editDescription: value }))}
              placeholder={qrType === "Text" ? "Write your text here" : "Write your message"}
            />
          </>
        )}

        {qrType === "Message" && (
          <>
            <div className="qrl-edit-brand-summary">
              <div className="qrl-edit-brand-logo">
                {editQR.messageImagePreviewUrl || editQR.fileUrl || editQR.editPayload?.fileUrl ? (
                  <img src={editQR.messageImagePreviewUrl || editQR.fileUrl || editQR.editPayload?.fileUrl} alt={editQR.title || "Message picture"} />
                ) : (
                  <FileOutlined />
                )}
              </div>
              <div>
                <strong>Message Picture</strong>
                <span>{editQR.messageImageFile ? "New picture selected" : editQR.fileUrl || editQR.editPayload?.fileUrl ? "Picture uploaded" : "No picture uploaded"}</span>
              </div>
            </div>

            <label>{config.fileLabel || "Picture"}</label>
            <Input type="file" accept={config.fileAccept || ".jpg,.jpeg,.png"} onChange={selectMessageImage} />
          </>
        )}

        {config.payloadFields?.map((field) => (
          <React.Fragment key={field.key}>
            <label>{field.label}</label>
            <Input
              type={field.type || "text"}
              value={
                field.type === "date"
                  ? parseDisplayDate(editQR.editPayload?.[field.key] || "")
                  : field.key === "companyName"
                  ? editQR.brandCompanyName
                  : editQR.editPayload?.[field.key] || ""
              }
              placeholder={field.placeholder}
              onChange={(event) => updateEditPayload(field.key, field.type === "date" ? formatDisplayDate(event.target.value) : event.target.value)}
            />
          </React.Fragment>
        ))}

        {qrType === "File" && (
          <div className="qrl-edit-note">
            File replacement is handled by the uploaded file record. Title, style, status, and password can be edited here.
          </div>
        )}
      </>
    );
  };

  const updateStatus = async (record) => {
    const nextStatus = record.status === "Active" ? "InActive" : "Active";
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/Qr/${record.id}`,
        { status: nextStatus },
        { headers: getAuthHeaders() }
      );
      if (res.data?.error === false) {
        message.success(`QR ${nextStatus === "Active" ? "activated" : "inactivated"}`);
        fetchQRList();
      } else {
        message.error(res.data?.message || "Failed to update status");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const deleteQR = async (record) => {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/Qr/${record.id}`, {
        headers: getAuthHeaders(),
      });
      if (res.data?.error === false) {
        message.success(res.data?.message || "QR deleted");
        fetchQRList();
      } else {
        message.error(res.data?.message || "Failed to delete QR");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to delete QR");
    }
  };

  const renderTypeTag = (record) => {
    const qrType = getQRType(record);
    const isLink = isLinkType(qrType);

    return (
      <Tag
        icon={isLink ? <GlobalOutlined /> : <QrcodeOutlined />}
        className={`qrl-type-tag ${isLink ? "link" : "text"}`}
      >
        {qrType}
      </Tag>
    );
  };

  const renderStatusTag = (status) => (
    <Tag color={status === "Active" ? "green" : "red"} style={{borderRadius:100,fontSize:11}}>
      {status || "Active"}
    </Tag>
  );

  const renderActions = (record) => {
    const isActive = (record.status || "Active") === "Active";

    return (
      <>
        <Button size="small" icon={<EyeOutlined />} className="qrl-action-btn view" onClick={() => openDetail(record)} title="View" />
        <Button size="small" icon={<EditOutlined />} className="qrl-action-btn edit" onClick={() => openEdit(record)} title="Edit" />
        <Button size="small" icon={<DownloadOutlined />} className="qrl-action-btn download" onClick={() => handleDownload(record)} title="Download PNG" />
        <Button
          size="small"
          icon={isActive ? <PoweroffOutlined /> : <PlayCircleOutlined />}
          className={`qrl-action-btn ${isActive ? "inactivate" : "activate"}`}
          onClick={() => updateStatus(record)}
          title={isActive ? "Inactivate" : "Activate"}
        />
        <Popconfirm
          title="Delete QR code?"
          description="This will soft delete the QR code."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => deleteQR(record)}
        >
          <Button size="small" icon={<DeleteOutlined />} className="qrl-action-btn danger" title="Delete" />
        </Popconfirm>
      </>
    );
  };

  const renderModalQR = (record, size = 220) => (
    record.qrImageUrl ? (
      <>
        <img src={record.qrImageUrl} alt={record.title || "QR Code"} className="qrl-modal-img" />
        <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
          <QRCodeCanvas
            id={`qr-modal-canvas-${record.id || record.uniqueId || record.title}`}
            value={record.qrUrl || record.link || record.qrText || "https://bonoxi.com"}
            size={1024}
            fgColor="#1a1a2e"
            bgColor="#ffffff"
          />
        </div>
      </>
    ) : (
      <QRCodeCanvas
        value={record.qrUrl || record.link || record.qrText || "https://bonoxi.com"}
        size={size}
        fgColor="#1a1a2e"
        bgColor="#ffffff"
      />
    )
  );

  const renderTextDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const textContent = selectedQR.qrText || payload.text || "";
    const publicQRUrl = getPublicQRUrl(selectedQR);

    return (
      <div className="qrl-text-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content">
          <div className="qrl-text-detail-header">
            <span>Text Content</span>
            <strong>{selectedQR.title || "Text QR"}</strong>
          </div>
          <div
            className="qrl-text-document-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(textContent || "No content available.") }}
          />
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderMessageDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const subject = payload.subject || selectedQR.title || "Message";
    const description = selectedQR.qrText || payload.description || "";
    const eventDate = payload.eventDate || "";
    const publicQRUrl = getPublicQRUrl(selectedQR);

    return (
      <div className="qrl-text-detail qrl-message-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-message-detail-content">
          <div className="qrl-text-detail-header qrl-message-detail-header">
            <span>Message Content</span>
            <strong>{subject}</strong>
            {eventDate && <em>{eventDate}</em>}
          </div>
          <div className="qrl-message-document-preview">
            <span>Message</span>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(description || "No message available.") }} />
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderProductDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const productUrl = selectedQR.link || payload.productUrl || payload.url || "";
    const companyName = payload.companyName || "";
    const sku = payload.sku || "";
    const platform = getProductPlatform(payload.platform);

    return (
      <div className="qrl-text-detail qrl-product-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-product-detail-content">
          <div className="qrl-text-detail-header qrl-product-detail-header">
            <span>Product QR</span>
            <strong>{selectedQR.title || "Product"}</strong>
            {companyName && <em>{companyName}</em>}
          </div>
          <div className="qrl-product-detail-body">
            <div className="qrl-product-hero-icon"><ShoppingOutlined /></div>
            <div className="qrl-product-info-grid">
              {sku && <div><span>SKU</span><strong>{sku}</strong></div>}
              {platform && <div><span>Platform</span><strong>{platform}</strong></div>}
              <div><span>Size</span><strong>{selectedQR.size || "Medium"}</strong></div>
              <div><span>Source</span><strong>{selectedQR.source || "Web"}</strong></div>
            </div>
            {productUrl && (
              <div className="qrl-product-url-box">
                <span>Product URL</span>
                <a href={productUrl} target="_blank" rel="noopener noreferrer">
                  <GlobalOutlined /> {productUrl}
                </a>
              </div>
            )}
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderSocialDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const platform = payload.platform || "Social";
    const companyName = payload.companyName || selectedQR.title || platform;
    const socialUrl = selectedQR.link || payload.socialUrl || payload.url || "";

    return (
      <div className="qrl-text-detail qrl-social-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-social-detail-content">
          <div className="qrl-social-profile">
            <div className="qrl-social-orbit one" />
            <div className="qrl-social-orbit two" />
            <div className="qrl-social-avatar">{platform.trim().charAt(0).toUpperCase() || "S"}</div>
            <span>Social QR</span>
            <strong>{companyName}</strong>
            <em><ShareAltOutlined /> {platform}</em>
          </div>
          <div className="qrl-social-detail-body">
            <div className="qrl-social-url-box">
              <span>Social URL</span>
              <a href={socialUrl} target="_blank" rel="noopener noreferrer">
                <GlobalOutlined /> {socialUrl || "No URL available"}
              </a>
            </div>
            <div className="qrl-social-info-grid">
              <div><span>Platform</span><strong>{platform}</strong></div>
              <div><span>Size</span><strong>{selectedQR.size || "Medium"}</strong></div>
              <div><span>Source</span><strong>{selectedQR.source || "Web"}</strong></div>
              <div><span>Protected</span><strong>{selectedQR.isPasswordProtected ? "Yes" : "No"}</strong></div>
            </div>
            {socialUrl && (
              <a className="qrl-social-open-btn" href={socialUrl} target="_blank" rel="noopener noreferrer">
                <ShareAltOutlined /> Open {platform}
              </a>
            )}
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderBrandDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const companyName = payload.companyName || selectedQR.title || "Brand";
    const websiteUrl = selectedQR.link || payload.websiteUrl || payload.url || "";

    return (
      <div className="qrl-text-detail qrl-brand-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-brand-detail-content">
          <div className="qrl-brand-profile">
            <div className="qrl-brand-glow" />
            <div className="qrl-brand-logo">
              {selectedQR.logoUrl ? <img src={selectedQR.logoUrl} alt={companyName} /> : <GlobalOutlined />}
            </div>
            <span>Brand QR</span>
            <strong>{companyName}</strong>
            <em>{selectedQR.title || "Brand"}</em>
          </div>
          <div className="qrl-brand-detail-body">
            <div className="qrl-brand-url-box">
              <span>Website URL</span>
              <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                <GlobalOutlined /> {websiteUrl || "No URL available"}
              </a>
            </div>
            <div className="qrl-brand-info-grid">
              <div><span>Size</span><strong>{selectedQR.size || "Medium"}</strong></div>
              <div><span>Source</span><strong>{selectedQR.source || "Web"}</strong></div>
              <div><span>Protected</span><strong>{selectedQR.isPasswordProtected ? "Yes" : "No"}</strong></div>
              <div><span>Logo</span><strong>{selectedQR.logoUrl ? "Uploaded" : "Not added"}</strong></div>
            </div>
            {websiteUrl && (
              <a className="qrl-brand-open-btn" href={websiteUrl} target="_blank" rel="noopener noreferrer">
                <GlobalOutlined /> Visit Website
              </a>
            )}
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderLocationDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const venueName = payload.venueName || selectedQR.title || "Location";
    const address = payload.address || "";
    const mapsUrl = selectedQR.link || payload.mapsUrl || payload.url || "";

    return (
      <div className="qrl-text-detail qrl-location-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-location-detail-content">
          <div className="qrl-location-map-hero">
            <div className="qrl-location-map-lines" />
            <div className="qrl-location-pin"><EnvironmentOutlined /></div>
            <span>Location QR</span>
            <strong>{venueName}</strong>
          </div>
          <div className="qrl-location-detail-body">
            {address && (
              <div className="qrl-location-address-box">
                <span>Address</span>
                <strong>{address}</strong>
              </div>
            )}
            <div className="qrl-location-info-grid">
              <div><span>Size</span><strong>{selectedQR.size || "Medium"}</strong></div>
              <div><span>Source</span><strong>{selectedQR.source || "Web"}</strong></div>
              <div><span>Protected</span><strong>{selectedQR.isPasswordProtected ? "Yes" : "No"}</strong></div>
              <div><span>Status</span><strong>{selectedQR.status || "Active"}</strong></div>
            </div>
            {mapsUrl && (
              <a className="qrl-location-open-btn" href={mapsUrl} target="_blank" rel="noopener noreferrer">
                <EnvironmentOutlined /> Open Google Maps
              </a>
            )}
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderVCardDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const contactName = payload.name || selectedQR.title || "Contact";

    return (
      <div className="qrl-text-detail qrl-vcard-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-vcard-detail-content">
          <div className="qrl-vcard-profile">
            <div className="qrl-vcard-avatar">{getInitials(contactName)}</div>
            <span>vCard Contact</span>
            <strong>{contactName}</strong>
            {payload.designation && <em>{payload.designation}</em>}
            {payload.companyName && <div className="qrl-vcard-company"><IdcardOutlined /> {payload.companyName}</div>}
          </div>

          <div className="qrl-vcard-quick-actions">
            {payload.mobile && <a href={`tel:${payload.mobile}`}><PhoneOutlined /> Call</a>}
            {payload.email && <a href={`mailto:${payload.email}`}><MailOutlined /> Email</a>}
            {payload.website && <a href={payload.website} target="_blank" rel="noopener noreferrer"><GlobalOutlined /> Website</a>}
          </div>

          <div className="qrl-vcard-info-grid">
            {payload.mobile && <div><span>Mobile</span><strong>{payload.mobile}</strong></div>}
            {payload.email && <div><span>Email</span><strong>{payload.email}</strong></div>}
            {payload.website && <div><span>Website</span><strong>{payload.website}</strong></div>}
            {payload.address && <div><span>Address</span><strong>{payload.address}</strong></div>}
          </div>

          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderFileDetailModal = () => {
    const payload = parsePayload(selectedQR.payloadData);
    const publicQRUrl = getPublicQRUrl(selectedQR);
    const files = selectedQR.files?.length ? selectedQR.files : [{
      originalFileName: getFileName(selectedQR.fileUrl || payload.fileUrl),
      fileUrl: selectedQR.fileUrl || payload.fileUrl,
      fileExtension: "",
      fileSizeBytes: 0,
      status: selectedQR.status,
    }];
    const primaryFile = files[0] || {};
    const fileUrl = primaryFile.fileUrl || selectedQR.fileUrl || payload.fileUrl || "";
    const fileName = primaryFile.originalFileName || getFileName(fileUrl);
    const fileExt = getFileExtension(primaryFile);

    return (
      <div className="qrl-text-detail qrl-file-detail">
        <div className="qrl-text-detail-qr">
          <div className="qrl-modal-qr">{renderModalQR(selectedQR)}</div>
          <div className="qrl-text-detail-badges">
            {renderTypeTag(selectedQR)}
            {renderStatusTag(selectedQR.status)}
            {selectedQR.isPasswordProtected && <Tag color="gold" style={{borderRadius:100,fontSize:11}}>Protected</Tag>}
          </div>
          {publicQRUrl && (
            <div className="qrl-detail-link">
              <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">{publicQRUrl}</a>
              <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                <CopyOutlined />
              </button>
            </div>
          )}
          <Button
            type="primary"
            block
            icon={<DownloadOutlined />}
            className="qrl-modal-download"
            onClick={() => handleDownload(selectedQR)}
          >
            Download QR PNG
          </Button>
        </div>

        <div className="qrl-text-detail-content qrl-file-detail-content">
          <div className="qrl-text-detail-header qrl-file-detail-header">
            <span>File QR</span>
            <strong>{selectedQR.title || "Shared File"}</strong>
            <em>{fileName}</em>
          </div>
          <div className="qrl-file-detail-body">
            <div className="qrl-file-preview">
              {isImageFile(fileUrl) ? (
                <img src={fileUrl} alt={fileName} />
              ) : (
                <div className="qrl-file-doc-icon"><FileOutlined /><span>{fileExt}</span></div>
              )}
            </div>
            <div className="qrl-file-info-grid">
              <div><span>File Name</span><strong>{fileName}</strong></div>
              <div><span>Type</span><strong>{fileExt}</strong></div>
              <div><span>Size</span><strong>{formatFileSize(primaryFile.fileSizeBytes)}</strong></div>
              <div><span>Status</span><strong>{primaryFile.status || selectedQR.status || "Active"}</strong></div>
            </div>
            {fileUrl && (
              <a className="qrl-file-open-btn" href={fileUrl} target="_blank" rel="noopener noreferrer">
                <DownloadOutlined /> Open File
              </a>
            )}
          </div>
          <div className="qrl-text-detail-stats">
            <div><span>Scans</span><strong>{selectedQR.scanCount || 0}</strong></div>
            <div><span>Downloads</span><strong>{selectedQR.downloadCount || 0}</strong></div>
            <div><span>Created</span><strong>{formatDate(selectedQR.createdAt)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const tableColumns = [
    {
      title: "QR",
      dataIndex: "qrImageUrl",
      width: 82,
      render: (_, record) => (
        <button className="qrl-table-preview" type="button" onClick={() => openDetail(record)}>
          {record.qrImageUrl ? (
            <>
              <img src={record.qrImageUrl} alt={record.title || "QR Code"} />
              <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
                <QRCodeCanvas
                  id={`qr-table-canvas-${record.id || record.uniqueId || record.title}`}
                  value={record.qrUrl || record.link || record.qrText || "https://bonoxi.com"}
                  size={1024}
                  fgColor="#1a1a2e"
                  bgColor="#ffffff"
                />
              </div>
            </>
          ) : (
            <QRCodeCanvas
              id={`qr-table-canvas-${record.id || record.title}`}
              value={record.qrUrl || record.link || record.qrText || "https://bonoxi.com"}
              size={48}
              fgColor="#1a1a2e"
              bgColor="#ffffff"
            />
          )}
        </button>
      ),
    },
    {
      title: "Name",
      dataIndex: "title",
      render: (_, record) => {
        const publicQRUrl = getPublicQRUrl(record);
        return (
          <div className="qrl-table-name">
            <strong>{record.title || "Untitled QR"}</strong>
            {publicQRUrl && (
              <div className="qrl-link-row">
                <a href={publicQRUrl} target="_blank" rel="noopener noreferrer">
                  <LinkOutlined /> {publicQRUrl}
                </a>
                <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                  <CopyOutlined />
                </button>
              </div>
            )}
          </div>
        );
      },
    },
    { title: "Type", width: 130, render: (_, record) => renderTypeTag(record) },
    { title: "Status", dataIndex: "status", width: 110, render: renderStatusTag },
    { title: "Scans", dataIndex: "scanCount", width: 82, render: (value) => value || 0 },
    { title: "Created", dataIndex: "createdAt", width: 132, render: formatDate },
    {
      title: "Actions",
      width: 250,
      render: (_, record) => <div className="qrl-table-actions">{renderActions(record)}</div>,
    },
  ];

  return (
    <div className="qrl-root">
      <div className="qrl-header">
        <div>
          <div className="qrl-title">My QR Codes</div>
          <div className="qrl-sub">{total} QR code{total !== 1 ? "s" : ""} generated</div>
        </div>
        <div className="qrl-toolbar">
          <Select
            value={filters.type}
            onChange={(type) => setFilters((prev) => ({ ...prev, type }))}
            className="qrl-filter"
            options={[
              { value: "", label: "All Types" },
              ..."Website,Text,Location,Brand,Social,File,vCard,Product,Message".split(",").map((type) => ({ value: type, label: type })),
            ]}
          />
          <Select
            value={filters.status}
            onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
            className="qrl-filter"
            options={[
              { value: "", label: "All Status" },
              { value: "Active", label: "Active" },
              { value: "InActive", label: "InActive" },
            ]}
          />
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            className="qrl-view-toggle"
            options={[
              { label: "Grid", value: "Grid", icon: <AppstoreOutlined /> },
              { label: "Table", value: "Table", icon: <TableOutlined /> },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchQRList} loading={loading} className="qrl-refresh">Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="qrl-skeleton">
          {[1,2,3].map((n) => <div key={n} className="qrl-skeleton-card" />)}
        </div>
      ) : data.length === 0 ? (
        <div className="qrl-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No QR codes yet" />
          <p className="qrl-empty-hint">Generate your first QR code using Link to QR or Text to QR.</p>
        </div>
      ) : viewMode === "Table" ? (
        <Table
          className="qrl-table"
          columns={tableColumns}
          dataSource={data}
          rowKey={(record, index) => record.id || record.uniqueId || record.title || index}
          pagination={false}
          scroll={{ x: 900 }}
        />
      ) : (
        <div className="qrl-grid">
          {data.map((record, i) => {
            const qrValue = record.qrUrl || record.link || record.qrText || "";
            const publicQRUrl = getPublicQRUrl(record);
            const id = record.id || record.title || i;
            return (
              <div key={id} className="qrl-card">
                <div className="qrl-card-preview" onClick={() => openDetail(record)}>
                  {record.qrImageUrl ? (
                    <>
                      <img src={record.qrImageUrl} alt={record.title || "QR Code"} className="qrl-card-img" />
                      <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
                        <QRCodeCanvas
                          id={`qr-canvas-${id}`}
                          value={qrValue || "https://bonoxi.com"}
                          size={1024}
                          fgColor="#1a1a2e"
                          bgColor="#ffffff"
                        />
                      </div>
                    </>
                  ) : (
                    <QRCodeCanvas
                      id={`qr-canvas-${id}`}
                      value={qrValue || "https://bonoxi.com"}
                      size={120}
                      fgColor="#1a1a2e"
                      bgColor="#ffffff"
                    />
                  )}
                </div>
                <div className="qrl-card-body">
                  <div className="qrl-card-title">{record.title || "Untitled QR"}</div>
                  {publicQRUrl && (
                    <div className="qrl-link-row qrl-card-link-row">
                      <a className="qrl-card-value qrl-card-link" href={publicQRUrl} target="_blank" rel="noopener noreferrer">
                        <LinkOutlined /> {publicQRUrl}
                      </a>
                      <button className="qrl-copy-link-btn" type="button" onClick={(event) => copyQRLink(event, publicQRUrl)} title="Copy QR link">
                        <CopyOutlined />
                      </button>
                    </div>
                  )}
                  <div className="qrl-card-meta">
                    {renderTypeTag(record)}
                    <span className="qrl-date"><CalendarOutlined /> {formatDate(record.createdAt)}</span>
                    {renderStatusTag(record.status)}
                  </div>
                </div>
                <div className="qrl-card-actions">
                  {renderActions(record)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        open={Boolean(selectedQR)}
        onCancel={() => setSelectedQR(null)}
        footer={null}
        width={["Text", "Message", "Product", "Social", "Brand", "Location", "vCard", "File"].includes(getQRType(selectedQR || {})) ? 780 : 480}
        title={<span style={{fontFamily:"Poppins",fontWeight:700}}>{selectedQR?.title || "QR Code"}</span>}
        centered
      >
        {selectedQR && getQRType(selectedQR) === "Text" ? renderTextDetailModal() : selectedQR && getQRType(selectedQR) === "Message" ? renderMessageDetailModal() : selectedQR && getQRType(selectedQR) === "Product" ? renderProductDetailModal() : selectedQR && getQRType(selectedQR) === "Social" ? renderSocialDetailModal() : selectedQR && getQRType(selectedQR) === "Brand" ? renderBrandDetailModal() : selectedQR && getQRType(selectedQR) === "Location" ? renderLocationDetailModal() : selectedQR && getQRType(selectedQR) === "vCard" ? renderVCardDetailModal() : selectedQR && getQRType(selectedQR) === "File" ? renderFileDetailModal() : selectedQR && (
          <div className="qrl-modal-body">
            <div className="qrl-modal-qr">
              {renderModalQR(selectedQR)}
            </div>
            <Divider />
            <Descriptions column={1} size="small" labelStyle={{fontWeight:600,color:"#888",fontSize:12}} contentStyle={{fontSize:13}}>
              <Descriptions.Item label="Type">{selectedQR.qrType || (selectedQR.link ? "Website" : "Text")}</Descriptions.Item>
              <Descriptions.Item label="Status">{selectedQR.status}</Descriptions.Item>
              <Descriptions.Item label="Scans">{selectedQR.scanCount || 0}</Descriptions.Item>
              <Descriptions.Item label="Downloads">{selectedQR.downloadCount || 0}</Descriptions.Item>
              <Descriptions.Item label="Password Protected">{selectedQR.isPasswordProtected ? "Yes" : "No"}</Descriptions.Item>
              <Descriptions.Item label="Created">{formatDate(selectedQR.createdAt)}</Descriptions.Item>
              {selectedQR.link && (
                <Descriptions.Item label="URL">
                  <a href={selectedQR.link} target="_blank" rel="noopener noreferrer" style={{color:"#e5322d",wordBreak:"break-all"}}>{selectedQR.link}</a>
                </Descriptions.Item>
              )}
              {!selectedQR.link && selectedQR.qrText && (
                <Descriptions.Item label="Text">{selectedQR.qrText}</Descriptions.Item>
              )}
            </Descriptions>
            <Button
              type="primary"
              block
              icon={<DownloadOutlined />}
              style={{marginTop:16,borderRadius:10,height:42}}
              onClick={() => handleDownload(selectedQR)}
            >
              Download QR PNG
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(editQR)}
        onCancel={() => {
          setEditQR(null);
          setEditPasswordError("");
        }}
        onOk={saveEdit}
        confirmLoading={saving}
        okText="Save Changes"
        width={560}
        title={<span style={{fontFamily:"Poppins",fontWeight:700}}>Edit QR</span>}
        centered
      >
        {editQR && (
          <div className="qrl-edit-form">
            <label>Title</label>
            <Input value={editQR.title} onChange={(event) => setEditQR((prev) => ({ ...prev, title: event.target.value }))} />

            {renderEditTypeFields()}

            <div className="qrl-edit-grid">
              <div>
                <label>Size</label>
                <Select
                  value={editQR.size || "Medium"}
                  onChange={(size) => setEditQR((prev) => ({ ...prev, size }))}
                  options={["Small", "Medium", "Large"].map((size) => ({ value: size, label: size }))}
                />
              </div>
              <div>
                <label>Status</label>
                <Select
                  value={editQR.status || "Active"}
                  onChange={(status) => setEditQR((prev) => ({ ...prev, status }))}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "InActive", label: "InActive" },
                  ]}
                />
              </div>
            </div>

            <div className="qrl-edit-grid">
              <div>
                <label>Color</label>
                <div className="qrl-edit-color-row">
                  {QR_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`qrl-edit-color-swatch ${editQR.color === color ? "active" : ""}`}
                      style={{ background: color }}
                      onClick={() => setEditQR((prev) => ({ ...prev, color }))}
                      type="button"
                      title={color}
                    />
                  ))}
                  <Input type="color" className="qrl-edit-color-custom" value={editQR.color || "#000000"} onChange={(event) => setEditQR((prev) => ({ ...prev, color: event.target.value }))} />
                </div>
              </div>
              <div>
                <label>Background</label>
                <div className="qrl-edit-color-row">
                  {QR_BACKGROUND_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`qrl-edit-color-swatch ${editQR.backgroundColor === color ? "active" : ""}`}
                      style={{ background: color }}
                      onClick={() => setEditQR((prev) => ({ ...prev, backgroundColor: color }))}
                      type="button"
                      title={color}
                    />
                  ))}
                  <Input type="color" className="qrl-edit-color-custom" value={editQR.backgroundColor || "#FFFFFF"} onChange={(event) => setEditQR((prev) => ({ ...prev, backgroundColor: event.target.value }))} />
                </div>
              </div>
            </div>

            <Divider style={{margin:"8px 0"}} />
            <label>Set / Change Password</label>
            <Input.Password
              status={editPasswordError ? "error" : ""}
              value={editQR.password}
              placeholder="Optional password"
              onChange={(event) => {
                setEditPasswordError("");
                setEditQR((prev) => ({ ...prev, password: event.target.value }));
              }}
            />
            {editQR.password && (
              <>
                <label>Confirm Password</label>
                <Input.Password
                  status={editPasswordError ? "error" : ""}
                  value={editQR.confirmPassword}
                  placeholder="Confirm password"
                  onChange={(event) => {
                    setEditPasswordError("");
                    setEditQR((prev) => ({ ...prev, confirmPassword: event.target.value }));
                  }}
                />
              </>
            )}
            {editPasswordError && <div className="qrl-edit-error">{editPasswordError}</div>}
            {editQR.isPasswordProtected && (
              <Checkbox
                checked={editQR.removePasswordProtection}
                onChange={(event) => setEditQR((prev) => ({ ...prev, removePasswordProtection: event.target.checked }))}
              >
                Remove password protection
              </Checkbox>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QRList;
